import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { Exercise } from '../../types';

interface ExerciseAutocompleteProps {
  exercises: Exercise[];
  value: Exercise | null;
  onChange: (exercise: Exercise | null) => void;
  onCreateExercise?: (name: string) => Promise<Exercise | null>;
  label?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
}

export const ExerciseAutocomplete: React.FC<ExerciseAutocompleteProps> = ({
  exercises,
  value,
  onChange,
  onCreateExercise,
  label,
  fullWidth = true,
  disabled = false,
  size = 'medium',
  error = false,
  helperText
}) => {
  const { t } = useLanguage();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingExerciseName, setPendingExerciseName] = useState('');

  const handleExerciseChange = async (event: any, newValue: string | Exercise | null) => {
    if (typeof newValue === 'string') {
      // User typed a new exercise name - show confirmation dialog if onCreateExercise is provided
      if (onCreateExercise) {
        setPendingExerciseName(newValue);
        setConfirmDialogOpen(true);
      }
    } else if (newValue && newValue.id === -1) {
      // User clicked on "Add [exercise name]" option - show confirmation dialog
      if (onCreateExercise) {
        const exerciseName = (newValue as any).originalName || newValue.name;
        setPendingExerciseName(exerciseName);
        setConfirmDialogOpen(true);
      }
    } else {
      // User selected an existing exercise or cleared selection
      onChange(newValue);
    }
  };

  const handleConfirmCreateExercise = async () => {
    if (onCreateExercise && pendingExerciseName) {
      const createdExercise = await onCreateExercise(pendingExerciseName);
      if (createdExercise) {
        onChange(createdExercise);
      }
    }
    setConfirmDialogOpen(false);
    setPendingExerciseName('');
  };

  const handleCancelCreateExercise = () => {
    setConfirmDialogOpen(false);
    setPendingExerciseName('');
  };

  const getOptionLabel = (option: string | Exercise) => {
    if (typeof option === 'string') {
      return option;
    }
    return option.name;
  };

  const filterOptions = (options: Exercise[], params: any) => {
    const filtered = options.filter((option) =>
      option.name.toLowerCase().includes(params.inputValue.toLowerCase())
    );

    const { inputValue } = params;
    // Only suggest creation if onCreateExercise is provided
    if (onCreateExercise) {
      const isExisting = options.some((option) => inputValue === option.name);
      if (inputValue !== '' && !isExisting) {
        filtered.push({
          id: -1,
          name: t('exercise.addOption', { name: inputValue }),
          originalName: inputValue, // Store the original input for extraction
        } as Exercise & { originalName: string });
      }
    }

    return filtered;
  };

  return (
    <>
      <Autocomplete
        options={Array.isArray(exercises) ? exercises : []}
        getOptionLabel={getOptionLabel}
        value={value}
        onChange={handleExerciseChange}
        filterOptions={filterOptions}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        disabled={disabled}
        size={size}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label={label || t('workout.exercise')} 
            fullWidth={fullWidth}
            error={error}
            helperText={helperText}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            {option.id === -1 ? (
              <Box sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                {option.name}
              </Box>
            ) : (
              option.name
            )}
          </Box>
        )}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        freeSolo={!!onCreateExercise}
      />

      {/* Exercise Creation Confirmation Dialog */}
      {onCreateExercise && (
        <Dialog open={confirmDialogOpen} onClose={handleCancelCreateExercise}>
          <DialogTitle>{t('exercise.confirmCreate')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('exercise.confirmCreateMessage', { name: pendingExerciseName })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelCreateExercise} color="primary">
              {t('workout.cancel')}
            </Button>
            <Button onClick={handleConfirmCreateExercise} color="primary" variant="contained">
              {t('exercise.create')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}; 